import type { Corpus, Section, SearchResult } from '../models/Corpus';
import { LibraryDB } from './LibraryDB';
import type { GutendexBook } from './Gutendex';
import { GutendexService } from './Gutendex';
import { fetchTextViaWorker, fetchArrayBufferViaWorker } from '../utils/fetchProxy';
import { cleanGutenbergText } from '../utils/gutenbergCleaner';
import { LocalImporter } from './LocalImporter';
import { parseHtmlCorpus } from './HtmlCorpusLoader';

export class PublicDomainCorpus implements Corpus {
    id = 'public_domain_gutendex';
    title = 'Public Domain (Gutendex)';
    author = 'Project Gutenberg';
    language = 'en';
    source = { type: 'internal' as const };

    // In-memory cache of valid sections (books)
    private sections: Section[] = [];
    // Content cache (bookId -> { chapterNum -> map of verses })
    private contentCache: Record<string, Record<string, Record<string, string>>> = {};

    async load() {
        const books = await LibraryDB.getAllBooks();

        this.sections = books.map(b => {
            let status = b.status;
            // Guardrail for existing "Zombie" PK-books
            if (b.status === 'ready' && b.textContent && b.textContent.startsWith('PK')) {
                console.warn(`[PublicDomainCorpus] Detected broken PK book: ${b.id}`);
                status = 'error';
            }

            return {
                id: b.id,
                title: b.title,
                chapterCount: b.chunkedChapters ? b.chunkedChapters.length : 0,
                status: status
            };
        });

        // Load content for all ready books
        for (const b of books) {
            // Skip loading content if it's broken (starts with PK)
            const isBroken = b.textContent && b.textContent.startsWith('PK');
            if (b.status === 'ready' && b.chunkedChapters && !isBroken) {
                this.contentCache[b.id] = {};
                b.chunkedChapters.forEach((text, i) => {
                    this.contentCache[b.id][String(i + 1)] = this.parseChapterText(text);
                });
            }
        }
        return true;
    }

    getSections(): Section[] {
        return this.sections;
    }

    // Helper: Split text into paragraphs and treat as verses
    private parseChapterText(text: string): Record<string, string> {
        const paragraphs = text.split(/\n\s*\n/);
        const verses: Record<string, string> = {};
        let v = 1;
        for (const p of paragraphs) {
            const clean = p.trim();
            if (clean) {
                verses[v.toString()] = clean;
                v++;
            }
        }
        return verses;
    }

    getChapter(sectionId: string, chapter: number): Record<string, string> | null {
        return this.contentCache[sectionId]?.[String(chapter)] || null;
    }

    getUnitText(sectionId: string, chapter: number) {
        const c = this.getChapter(sectionId, chapter);
        if (!c) return null;
        return Object.values(c).join("\n");
    }

    async search(query: string): Promise<SearchResult[]> {
        // Local text search
        const results: SearchResult[] = [];
        const q = query.toLowerCase();

        for (const sec of this.sections) {
            const bookCache = this.contentCache[sec.id];
            if (!bookCache) continue;

            Object.entries(bookCache).forEach(([chStr, verses]) => {
                const ch = parseInt(chStr);
                Object.entries(verses).forEach(([vStr, text]) => {
                    if (text.toLowerCase().includes(q)) {
                        results.push({
                            vid: `${sec.id}-${ch}-${vStr}`,
                            bookId: sec.id,
                            bookName: sec.title,
                            chapter: ch,
                            verse: parseInt(vStr),
                            text: text
                        });
                    }
                });
            });
        }

        return results;
    }

    resolveSectionId(slug: string) {
        return this.sections.find(s => s.id === slug || s.title === slug)?.id;
    }

    // --- Special Methods for UI integration ---

    async downloadBook(gutendexBook: GutendexBook): Promise<void> {
        // 1. Get Candidates
        const candidates = GutendexService.getDownloadCandidates(gutendexBook);
        if (candidates.length === 0) throw new Error("No suitable format found (Text or HTML).");

        // 2. Add metadata (Optimistic)
        await LibraryDB.addBook({
            id: gutendexBook.id.toString(),
            title: gutendexBook.title,
            authors: gutendexBook.authors,
            languages: gutendexBook.languages,
            coverUrl: gutendexBook.formats['image/jpeg'],
            formats: gutendexBook.formats,
            addedAt: Date.now(),
            preferredFormatUrl: candidates[0].url,
            status: 'downloading'
        });

        // 3. Try candidates in order
        let bestText: string | null = null;
        let finalChapters: string[] | null = null;
        const errors: string[] = [];

        for (const cand of candidates) {
            console.debug(`[PublicDomainCorpus] Attempting format: ${cand.mime} URL: ${cand.url}`);
            try {
                if (cand.mime === 'application/epub+zip') {
                    // HANDLE EPUB: Binary fetch + Client-side parse
                    const buffer = await fetchArrayBufferViaWorker(cand.url);

                    // Use shared logic from LocalImporter
                    const result = await LocalImporter.processEpubData(new Uint8Array(buffer), gutendexBook.title);

                    bestText = result.fullText;
                    finalChapters = result.chapters;
                    // Note: We ignore result.coverUrl here as we already have one from Gutendex

                } else {
                    // HANDLE TEXT/HTML
                    const rawText = await fetchTextViaWorker(cand.url);

                    // Validation: Check for Gutenberg "Not Found" HTML page (Soft 404)
                    if (rawText.length < 5000 && (rawText.includes("<title>404 Not Found</title>") || rawText.includes("The requested URL was not found"))) {
                        throw new Error(`Soft 404 (content mismatch)`);
                    }

                    // HARD GUARDRAIL: Check for PK header in "text"
                    if (rawText.startsWith('PK')) {
                        throw new Error(`Detected binary content (ZIP/EPUB) in text path. Aborting this candidate.`);
                    }
                    if (rawText.includes('META-INF/container.xml')) {
                        throw new Error(`Detected EPUB structure in text path. Aborting.`);
                    }

                    let cleanText = rawText;
                    if (cand.mime.includes('html')) {
                        // Use smart HTML parsing
                        const units = await parseHtmlCorpus(rawText);
                        // parseHtmlCorpus now handles fallback to single chapter if no headings
                        bestText = units.map(u => u.text).join('\n\n');
                        finalChapters = units.map(u => u.text);
                    } else {
                        // Basic cleaning for raw text files
                        let cleanText = cleanGutenbergText(rawText);
                        bestText = cleanText;
                        // NO chunking for TXT - treat as one single chapter
                        finalChapters = [cleanText];
                    }
                }

                break; // Success!
            } catch (e: any) {
                const msg = `[${cand.mime}] ${cand.url} => ${e.message}`;
                console.warn(msg);
                errors.push(msg);
            }
        }

        if (!bestText || !finalChapters) {
            await LibraryDB.updateBookStatus(gutendexBook.id.toString(), 'error', undefined, undefined, {
                source: 'gutendex',
                format: 'unknown',
                fallback: true,
                shortReason: errors.slice(-3).join('\n'),
                timestamp: Date.now()
            });
            const recentErrors = errors.slice(-3).join('\n');
            throw new Error(`Download failed. Last attempts:\n${recentErrors}`);
        }

        try {
            // 4. Save
            // Save raw text as asset? (Maybe skipping for now to save space, or just save bestText)
            // await LibraryDB.saveBookAsset(gutendexBook.id.toString(), bestText);

            // 5. Update DB
            await LibraryDB.updateBookStatus(gutendexBook.id.toString(), 'ready', bestText, finalChapters, {
                source: 'gutendex',
                format: bestText.startsWith('PK') ? 'epub_binary_error' : 'text_or_epub_parsed',
                timestamp: Date.now()
            });

            // Reload into memory
            await this.load();

        } catch (e) {
            console.error(e);
            await LibraryDB.updateBookStatus(gutendexBook.id.toString(), 'error');
            throw e;
        }
    }

    private extractTextFromHtml(html: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        // Remove scripts and styles
        const scripts = doc.querySelectorAll('script, style');
        scripts.forEach(s => s.remove());
        return doc.body.innerText || "";
    }
}
